import type { PreparationState, PreparationStep, Tenant } from '@bk/contracts';
import { denominazioneLeggibile } from '@bk/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { api, apiUpload, errorMessage } from '../lib/api';
import { navigate } from '../lib/router';
import { Button, Card, ErrorText } from '../ui';
import { SediConferma } from './SediConferma';

/** I passi della Fase 0 come li legge il titolare (ADR 0201). */
const LABEL: Record<PreparationStep['passo'], string> = {
  identita: 'L’impresa nel Registro Imprese',
  visura: 'La visura camerale',
  lettura: 'Lettura della visura: sedi, attività ATECO, cariche e soci',
  vies: 'Partita IVA su VIES',
  marchio: 'Logo e colori dal sito ufficiale',
  sedi: 'Le sedi dell’azienda, confermate da te',
  gestionale: 'Il gestionale su misura dalla libreria ATECO',
  ruoli: 'I ruoli dell’azienda dalla libreria',
  procedure: 'Le procedure dell’azienda dalla libreria',
};

const day = (iso: string) => new Date(`${iso}T00:00:00`).toLocaleDateString('it-IT', { day: 'numeric', month: 'long', year: 'numeric' });

function StepRow({ p }: { p: PreparationStep }) {
  const icon = p.stato === 'fatto' ? '✓' : p.stato === 'errore' ? '!' : p.stato === 'saltato' ? '–' : p.stato === 'in_corso' ? '…' : '·';
  const tone = p.stato === 'fatto' ? 'bg-emerald-100 text-emerald-800' : p.stato === 'errore' ? 'bg-red-100 text-red-800' : p.stato === 'in_corso' ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-500';
  return (
    <li className="flex items-start gap-3 py-2">
      <span aria-hidden className={`mt-0.5 grid h-6 w-6 shrink-0 place-items-center rounded-full text-xs font-bold ${tone}`}>
        {icon}
      </span>
      <div className="min-w-0">
        <p className={`text-sm font-medium ${p.stato === 'in_attesa' ? 'text-ink/50' : 'text-ink'}`}>{LABEL[p.passo]}</p>
        {p.nota && <p className="text-xs text-ink/55">{p.nota}</p>}
      </div>
    </li>
  );
}

/** La scelta della visura (ADR 0200): caricarla o farla cercare. Farlo conferma l'impresa trovata nel registro. */
function VisuraChoice({ tenantId, s }: { tenantId: string; s: PreparationState }) {
  const qc = useQueryClient();
  const input = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(null);
  const [old, setOld] = useState<string | null>(null);
  const done = (x: PreparationState) => qc.setQueryData(['tenant', tenantId, 'preparation'], x);
  const upload = useMutation({
    mutationFn: ({ f, anyway }: { f: File; anyway: boolean }) => apiUpload<PreparationState>(`/tenants/${tenantId}/onboarding/preparation/visura${anyway ? '?anche_se_vecchia=1' : ''}`, f),
    onSuccess: (x) => (x.visura_vecchia ? setOld(x.visura_vecchia) : done(x)),
  });
  const search = useMutation({
    mutationFn: () => api<PreparationState>(`/tenants/${tenantId}/onboarding/preparation/visura/cerca`, { method: 'POST' }),
    onSuccess: done,
  });
  const notMine = useMutation({
    mutationFn: () => api<PreparationState>(`/tenants/${tenantId}/onboarding/preparation/confirm`, { method: 'POST', body: { si: false } }),
    onSuccess: () => navigate('/riparti'),
  });
  const busy = upload.isPending || search.isPending || notMine.isPending;
  const pick = (f: File | undefined) => {
    if (!f) return;
    setFile(f);
    setOld(null);
    upload.mutate({ f, anyway: false });
  };
  const impresa = s.conferma ?? (s.trovati.ragione_sociale ? { denominazione: s.trovati.ragione_sociale, comune: null } : null);
  return (
    <Card className="space-y-4">
      <div>
        <h2 className="text-lg font-semibold text-ink">La visura camerale</h2>
        <p className="mt-1 text-sm text-ink/60">
          Dalla visura leggiamo sedi, attività con i codici ATECO, cariche e soci, e prepariamo il gestionale. Se ce l’hai in PDF caricala: non ne compriamo un’altra.
        </p>
      </div>
      {impresa && (
        <div className="rounded-lg border border-black/[0.07] bg-black/[0.02] px-3 py-2 text-sm">
          <p className="text-xs font-medium uppercase tracking-wide text-ink/45">Nel Registro Imprese</p>
          <p className="font-medium text-ink">
            {impresa.denominazione ? denominazioneLeggibile(impresa.denominazione) : 'Impresa trovata'}
            {impresa.comune ? ` · ${impresa.comune}` : ''}
          </p>
          {s.trovati.vies?.esito === 'valida' && <p className="text-xs text-ink/55">VIES: partita IVA attiva{s.trovati.vies.indirizzo ? ` · ${s.trovati.vies.indirizzo}` : ''}</p>}
        </div>
      )}
      {old && file ? (
        <div className="space-y-3 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
          <p>
            La visura è del {day(old)}: più vecchia di tre mesi, sedi e cariche potrebbero essere cambiate.
          </p>
          <div className="flex flex-wrap gap-2">
            <Button disabled={busy} onClick={() => upload.mutate({ f: file, anyway: true })}>
              Usala comunque
            </Button>
            <Button variant="ghost" disabled={busy} onClick={() => search.mutate()}>
              Cercane una aggiornata
            </Button>
          </div>
        </div>
      ) : (
        <div className="flex flex-wrap gap-2">
          <Button disabled={busy} onClick={() => input.current?.click()}>
            {upload.isPending ? 'Controllo la visura…' : 'Carica la visura (PDF)'}
          </Button>
          <Button variant="ghost" disabled={busy} onClick={() => search.mutate()}>
            {search.isPending ? 'Un momento…' : s.visura_cercabile ? 'Non ce l’ho: cercala tu' : 'Prosegui senza visura'}
          </Button>
          <input ref={input} id="visura-file" type="file" accept="application/pdf,.pdf" hidden onChange={(e) => pick(e.target.files?.[0])} />
        </div>
      )}
      <ErrorText>{upload.error || search.error || notMine.error ? errorMessage(upload.error ?? search.error ?? notMine.error) : ''}</ErrorText>
      <p className="text-xs text-ink/50">
        Controlliamo che sia una visura e che abbia la tua partita IVA.{' '}
        {s.visura_cercabile ? 'La ricerca nel Registro Imprese è a pagamento.' : 'Non possiamo cercarla noi per questa partita IVA: caricala, oppure prosegui e il gestionale nasce dalla sola base comune.'}
        {s.conferma && (
          <>
            {' '}
            <button type="button" className="underline" disabled={busy} onClick={() => notMine.mutate()}>
              Non è la mia impresa
            </button>
          </>
        )}
      </p>
    </Card>
  );
}

/**
 * La Fase 0 (ADR 0201): dopo la partita IVA, la visura caricata o cercata, la sua lettura, la libreria ATECO e il
 * gestionale su misura; quando è composto si entra nel gestionale, dove le domande stanno solo nei moduli del menu.
 */
export function PreparazionePage({ tenantId }: { tenantId: string }) {
  const qc = useQueryClient();
  const tenant = useQuery({
    queryKey: ['tenant', tenantId, 'self'],
    queryFn: () => api<Tenant>(`/tenants/${tenantId}`),
    refetchInterval: (q) => (q.state.data?.status === 'active' || q.state.data?.status === 'failed' ? false : 1500),
  });
  const active = tenant.data?.status === 'active';
  const prep = useQuery({
    queryKey: ['tenant', tenantId, 'preparation'],
    queryFn: () => api<PreparationState>(`/tenants/${tenantId}/onboarding/preparation`),
    enabled: active,
    refetchInterval: (q) => (q.state.data?.pronta ? false : 1500),
  });
  const retry = useMutation({
    mutationFn: () => api<PreparationState>(`/tenants/${tenantId}/onboarding/preparation/retry`, { method: 'POST' }),
    onSuccess: (s) => qc.setQueryData(['tenant', tenantId, 'preparation'], s),
  });
  const retryDb = useMutation({
    mutationFn: () => api<Tenant>(`/tenants/${tenantId}/provision`, { method: 'POST' }),
    onSettled: () => void qc.invalidateQueries({ queryKey: ['tenant', tenantId, 'self'] }),
  });
  const s = prep.data;
  const ready = !!s?.pronta && !s.rifiutata;
  const stopped = !!s?.passi.some((p) => p.stato === 'errore');
  useEffect(() => {
    // Con un passo non riuscito non si entra da soli: il titolare vede cosa manca e può riprovare.
    if (!ready || stopped) return;
    void qc.invalidateQueries({ queryKey: ['tenants'] });
    const timer = setTimeout(() => navigate(`/t/${tenantId}`), 1200);
    return () => clearTimeout(timer);
  }, [ready, stopped, tenantId, qc]);

  const failed = tenant.data?.status === 'failed';
  const name = s?.trovati.ragione_sociale ? denominazioneLeggibile(s.trovati.ragione_sociale) : (tenant.data?.name ?? '');
  return (
    <div className="mx-auto w-full max-w-2xl space-y-5 px-4 py-10">
      <div>
        <button type="button" className="text-sm text-ink/55 hover:underline" onClick={() => navigate('/aziende')}>
          ← Le tue aziende
        </button>
        <h1 className="mt-2 text-xl font-semibold text-ink">{name || 'La tua azienda'}</h1>
        {tenant.data?.vat_number && <p className="text-sm text-ink/55">P.IVA {tenant.data.vat_number}</p>}
      </div>

      {failed ? (
        <Card className="space-y-3">
          <p className="text-sm text-ink">{tenant.data?.provisioning_error ?? 'Il fascicolo dell’azienda non si è aperto.'}</p>
          {tenant.data?.role === 'owner' && (
            <Button disabled={retryDb.isPending} onClick={() => retryDb.mutate()}>
              Riprova
            </Button>
          )}
        </Card>
      ) : !active || !s ? (
        <Card>
          <p className="text-sm text-ink/60">Creo il fascicolo dell’azienda e cerco la partita IVA nel Registro Imprese…</p>
        </Card>
      ) : s.rifiutata ? (
        <Card className="space-y-3">
          <p className="text-sm text-ink">Hai detto che non è la tua impresa: nessuna visura è stata comprata.</p>
          <Button onClick={() => navigate('/riparti')}>Riparti dalla partita IVA giusta</Button>
        </Card>
      ) : (
        <>
          {s.scelta_visura && <VisuraChoice tenantId={tenantId} s={s} />}
          {s.sedi_da_confermare && <SediConferma tenantId={tenantId} />}
          <Card className="space-y-2">
            <h2 className="text-sm font-semibold uppercase tracking-wide text-ink/50">{ready ? 'Pronto: apro il gestionale' : 'Preparo il gestionale'}</h2>
            <ul className="divide-y divide-black/[0.05]">
              {s.passi.map((p) => (
                <StepRow key={p.passo} p={p} />
              ))}
            </ul>
            {stopped && (
              <div className="flex items-center gap-3 pt-2">
                <Button variant="ghost" disabled={retry.isPending} onClick={() => retry.mutate()}>
                  Riprova i passi non riusciti
                </Button>
              </div>
            )}
            {ready && (
              <Button onClick={() => navigate(`/t/${tenantId}`)}>Apri il gestionale</Button>
            )}
          </Card>
        </>
      )}
    </div>
  );
}
